import moment from "moment";
import { barEmotionOrder, emotionColors as defaultEmotionColors } from "../utils";

const MONTH_LABEL_LOCALE = "en-dashboard-month-labels";

if (!moment.locales().includes(MONTH_LABEL_LOCALE)) {
  moment.defineLocale(MONTH_LABEL_LOCALE, {
    parentLocale: "en",
    monthsShort: [
      "Jan.",
      "Feb.",
      "Mar.",
      "Apr.",
      "May",
      "Jun.",
      "Jul.",
      "Aug.",
      "Sep.",
      "Oct.",
      "Nov.",
      "Dec.",
    ],
  });
}

const TIME_INTERVAL_CONFIG = {
  "10 minutes": {
    count: 10,
    stepAmount: 1,
    stepUnit: "minute",
    subtract: [10, "minutes"],
    labelFormat: "h:mm A",
    labelEvery: 3,
    startOf: "minute",
  },
  "1 hour": {
    count: 12,
    stepAmount: 5,
    stepUnit: "minute",
    subtract: [1, "hour"],
    labelFormat: "h:mm A",
    labelEvery: 3,
    startOf: "minute",
  },
  "2 hours": {
    count: 12,
    stepAmount: 10,
    stepUnit: "minute",
    subtract: [2, "hours"],
    labelFormat: "h:mm A",
    labelEvery: 3,
    startOf: "minute",
  },
  "24 hours": {
    count: 24,
    stepAmount: 1,
    stepUnit: "hour",
    subtract: [24, "hours"],
    labelFormat: "ha",
    labelEvery: 3,
    startOf: "hour",
  },
  week: {
    count: 7,
    stepAmount: 1,
    stepUnit: "day",
    subtract: [7, "days"],
    labelFormat: "MMM D",
    labelEvery: 1,
  },
  month: {
    count: 30,
    stepAmount: 1,
    stepUnit: "day",
    subtract: [30, "days"],
    labelFormat: "MMM D",
    labelEvery: 7,
  },
  "3 months": {
    count: 13,
    stepAmount: 1,
    stepUnit: "week",
    subtract: [90, "days"],
    labelFormat: "MMM D",
    labelEvery: 1,
  },
  "6 months": {
    count: 13,
    stepAmount: 2,
    stepUnit: "week",
    subtract: [6, "months"],
    labelFormat: "MMM D",
    labelEvery: 2,
  },
  year: {
    count: 12,
    stepAmount: 1,
    stepUnit: "month",
    subtract: [1, "year"],
    labelFormat: "MMM",
    labelEvery: 1,
  },
  "2 years": {
    count: 24,
    stepAmount: 1,
    stepUnit: "month",
    subtract: [2, "years"],
    labelFormat: "MMM YYYY",
    labelEvery: 3,
  },
};

const DEFAULT_TIME = "month";

const AVERAGE_SLICE_CONFIG = {
  "10 minutes": { amount: 1, unit: "minute" },
  "1 hour": { amount: 5, unit: "minute" },
  "2 hours": { amount: 10, unit: "minute" },
  "24 hours": { amount: 1, unit: "hour" },
  week: { amount: 1, unit: "day" },
  month: { amount: 1, unit: "day" },
  "3 months": { amount: 1, unit: "day" },
  "6 months": { amount: 1, unit: "day" },
  year: { amount: 1, unit: "day" },
  "2 years": { amount: 1, unit: "day" },
};

const TIME_UNIT_MS = {
  minute: 60 * 1000,
  hour: 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
};

const getAverageSliceConfig = (chosenTime) =>
  AVERAGE_SLICE_CONFIG[chosenTime] || AVERAGE_SLICE_CONFIG[DEFAULT_TIME];

const createEmotionCounts = (emotionOrder, initialValue) =>
  emotionOrder.reduce((acc, emotion) => {
    acc[emotion] = initialValue;
    return acc;
  }, {});

const createStudentCounts = (emotionOrder) =>
  emotionOrder.reduce((acc, emotion) => {
    acc[emotion] = {};
    return acc;
  }, {});

const calculatePercentage = (count, total) =>
  total > 0 ? Number(((count / total) * 100).toFixed(1)) : 0;

const buildBucketAverages = (sliceBuckets, emotionOrder) => {
  const percentageTotals = createEmotionCounts(emotionOrder, 0);
  let averageSliceCount = 0;

  sliceBuckets.forEach((slice) => {
    if (!slice.total) return;

    averageSliceCount += 1;
    emotionOrder.forEach((emotion) => {
      percentageTotals[emotion] += calculatePercentage(
        slice.counts[emotion],
        slice.total,
      );
    });
  });

  return {
    averageSliceCount,
    percentagesByEmotion: emotionOrder.reduce((acc, emotion) => {
      acc[emotion] = averageSliceCount
        ? Number((percentageTotals[emotion] / averageSliceCount).toFixed(1))
        : 0;
      return acc;
    }, {}),
  };
};

const createAverageSlices = (interval, chosenTime, emotionOrder) => {
  const config = getAverageSliceConfig(chosenTime);
  const unitMs = TIME_UNIT_MS[config.unit];
  if (!unitMs) {
    return [{ total: 0, counts: createEmotionCounts(emotionOrder, 0) }];
  }

  const sliceMs = config.amount * unitMs;
  const sliceCount = Math.max(
    1,
    Math.ceil((interval.endMs - interval.startMs) / sliceMs),
  );

  return Array.from({ length: sliceCount }, () => ({
    total: 0,
    counts: createEmotionCounts(emotionOrder, 0),
  }));
};

const isTimestampInInterval = (timestampMs, startMs, endMs, includeEnd = false) =>
  timestampMs >= startMs && (includeEnd ? timestampMs <= endMs : timestampMs < endMs);

const getInclusiveDateEnd = (start, end) =>
  end.clone().subtract(1, "millisecond").isBefore(start)
    ? start.clone()
    : end.clone().subtract(1, "millisecond");

const formatRangeDate = (date, includeYear = false) =>
  date
    .clone()
    .locale(MONTH_LABEL_LOCALE)
    .format(includeYear ? "MMM D, YYYY" : "MMM D");

const formatTooltipRangeLabel = (chosenTime, interval) => {
  const start = interval.start.clone().locale(MONTH_LABEL_LOCALE);
  const end = interval.end.clone().locale(MONTH_LABEL_LOCALE);
  const inclusiveEnd = getInclusiveDateEnd(start, end);

  if (["10 minutes", "1 hour", "2 hours"].includes(chosenTime)) {
    return `${start.format("h:mm A")} - ${end.format("h:mm A")} Avg`;
  }

  if (chosenTime === "24 hours") {
    const endFormat = start.isSame(end, "day") ? "h A" : "MMM D, h A";
    return `${start.format("MMM D, h A")} - ${end.format(endFormat)} Avg`;
  }

  if (["week", "month"].includes(chosenTime)) {
    return `${formatRangeDate(start)} Avg`;
  }

  const includeYear =
    ["year", "2 years"].includes(chosenTime) ||
    !start.isSame(inclusiveEnd, "year");

  return `${formatRangeDate(start, includeYear)} - ${formatRangeDate(
    inclusiveEnd,
    includeYear,
  )} Avg`;
};

const createIntervals = (chosenTime, now) => {
  const config = TIME_INTERVAL_CONFIG[chosenTime] || TIME_INTERVAL_CONFIG[DEFAULT_TIME];
  const [amount, unit] = config.subtract;
  const start = now.clone().subtract(amount, unit).startOf(config.startOf || "day");

  return Array.from({ length: config.count }, (_, index) => {
    const intervalStart = start
      .clone()
      .add(index * config.stepAmount, config.stepUnit);
    const intervalEnd =
      index === config.count - 1
        ? now.clone()
        : start.clone().add((index + 1) * config.stepAmount, config.stepUnit);
    const labelStart = intervalStart.clone().locale(MONTH_LABEL_LOCALE);

    return {
      x: index,
      key: intervalStart.toISOString(),
      label:
        index % config.labelEvery === 0
          ? labelStart.format(config.labelFormat)
          : "",
      tooltipLabel: labelStart.format(config.labelFormat),
      tooltipDateLabel: formatTooltipRangeLabel(chosenTime, {
        start: intervalStart,
        end: intervalEnd,
      }),
      year: intervalStart.format("YYYY"),
      start: intervalStart.locale(MONTH_LABEL_LOCALE),
      end: intervalEnd.locale(MONTH_LABEL_LOCALE),
      startMs: intervalStart.valueOf(),
      endMs: intervalEnd.valueOf(),
    };
  });
};

const buildBucketIndexResolver = (intervals, chosenTime) => {
  const config = TIME_INTERVAL_CONFIG[chosenTime] || TIME_INTERVAL_CONFIG[DEFAULT_TIME];
  const firstStart = intervals[0];

  if (!firstStart) {
    return () => -1;
  }

  if (config.stepUnit === "month") {
    const firstStartDate = new Date(firstStart.startMs);

    return (timestampMs) => {
      const pointDate = new Date(timestampMs);
      let diff =
        (pointDate.getFullYear() - firstStartDate.getFullYear()) * 12 +
        (pointDate.getMonth() - firstStartDate.getMonth());

      if (
        pointDate.getDate() < firstStartDate.getDate() ||
        (pointDate.getDate() === firstStartDate.getDate() &&
          pointDate.getHours() < firstStartDate.getHours())
      ) {
        diff -= 1;
      }

      return Math.max(0, Math.min(intervals.length - 1, Math.floor(diff / config.stepAmount)));
    };
  }

  const unitMs = TIME_UNIT_MS[config.stepUnit];
  const stepMs = unitMs ? unitMs * config.stepAmount : 0;

  return (timestampMs) => {
    if (!stepMs) return -1;
    const diff = Math.floor((timestampMs - firstStart.startMs) / stepMs);
    return Math.max(0, Math.min(intervals.length - 1, diff));
  };
};

const getSliceIndex = (timestampMs, bucket, chosenTime) => {
  if (!bucket.sliceBuckets.length) return -1;

  const config = getAverageSliceConfig(chosenTime);
  const unitMs = TIME_UNIT_MS[config.unit];
  if (!unitMs) return 0;

  const sliceIndex = Math.floor(
    (timestampMs - bucket.startMs) / (config.amount * unitMs),
  );

  return Math.max(0, Math.min(bucket.sliceBuckets.length - 1, sliceIndex));
};

export const buildLineGraphData = ({
  lineGraphSourceData = [],
  chosenTime = DEFAULT_TIME,
  emotionOrder = barEmotionOrder,
  emotionColors = defaultEmotionColors,
  now = moment(),
}) => {
  const nowMoment = moment.isMoment(now) ? now.clone() : moment(now);
  const intervals = createIntervals(chosenTime, nowMoment);
  const emotionSet = new Set(emotionOrder);
  const getBucketIndex = buildBucketIndexResolver(intervals, chosenTime);
  const buckets = intervals.map((interval) => ({
    ...interval,
    total: 0,
    counts: createEmotionCounts(emotionOrder, 0),
    studentCounts: createStudentCounts(emotionOrder),
    sliceBuckets: createAverageSlices(interval, chosenTime, emotionOrder),
  }));

  lineGraphSourceData.forEach((point) => {
    const emotion = point?.emotion;
    if (!emotionSet.has(emotion)) return;

    const timestampMs = Date.parse(point.date || point.createdAt);
    if (Number.isNaN(timestampMs)) return;

    const bucketIndex = getBucketIndex(timestampMs);
    if (bucketIndex < 0) return;

    const bucket = buckets[bucketIndex];
    const isLastBucket = bucketIndex === buckets.length - 1;
    const isInBucketRange = isTimestampInInterval(
      timestampMs,
      bucket.startMs,
      bucket.endMs,
      isLastBucket,
    );

    if (!isInBucketRange) return;

    bucket.counts[emotion] += 1;
    bucket.total += 1;

    const sliceIndex = getSliceIndex(timestampMs, bucket, chosenTime);
    if (sliceIndex >= 0) {
      const slice = bucket.sliceBuckets[sliceIndex];
      slice.counts[emotion] += 1;
      slice.total += 1;
    }

    const studentKey = point.email || point.userId;
    if (studentKey) {
      bucket.studentCounts[emotion][studentKey] =
        (bucket.studentCounts[emotion][studentKey] || 0) + 1;
    }
  });

  const bucketAverages = buckets.map((bucket) =>
    buildBucketAverages(bucket.sliceBuckets, emotionOrder),
  );

  const tooltipRowsByInterval = buckets.map((bucket, index) =>
    emotionOrder.map((emotion) => {
      const count = bucket.counts[emotion];
      const { averageSliceCount, percentagesByEmotion } = bucketAverages[index];
      const percentage = percentagesByEmotion[emotion];
      const studentEntries = bucket.studentCounts[emotion] || {};

      return {
        emotion,
        count,
        percentage,
        total: bucket.total,
        averageSliceCount,
        color: emotionColors[emotion],
        studentCounts: studentEntries,
      };
    }),
  );

  const seriesByEmotion = emotionOrder.reduce((acc, emotion) => {
    acc[emotion] = buckets.map((bucket, index) => {
      const count = bucket.counts[emotion];
      const { averageSliceCount, percentagesByEmotion } = bucketAverages[index];
      const percentage = percentagesByEmotion[emotion];

      return {
        x: bucket.x,
        y: percentage,
        emotion,
        count,
        total: bucket.total,
        intervalLabel: bucket.tooltipLabel,
        tooltipDateLabel: bucket.tooltipDateLabel,
        averageSliceCount,
        year: bucket.year,
        tooltipRows: tooltipRowsByInterval[index],
      };
    });
    return acc;
  }, {});

  return {
    intervals: buckets.map(
      ({ x, key, label, tooltipLabel, tooltipDateLabel, year, start, end }) => ({
        x,
        key,
        label,
        tooltipLabel,
        tooltipDateLabel,
        year,
        start,
        end,
      }),
    ),
    seriesByEmotion,
    tooltipRowsByInterval,
    hasData: buckets.some((bucket) => bucket.total > 0),
  };
};
